import { useEffect, useState } from 'react';
import { useQuery } from 'react-apollo';

import { AlbumSlideshowForUnprocessedPhotos } from '../../../../../../graphql/queries/albums';
import { ALBUM_SLIDESHOW_MODE } from '../../../../../../constants/albums';
import { isMemoryBook } from '../../../../../../util/album';

const useAlbumSlideshowForUnprocessedPhotos = ({
  album,
  slideshowMode,
  slideshowLastUploadedAt,
  pollingIntervalMs,
}) => {
  const [totalPhotos, setTotalPhotos] = useState(undefined);
  const [lastUploadedAt, setLastUploadedAt] = useState(undefined);
  const [includePhotos, setIncludePhotos] = useState(false);
  const [hasRefetched, setHasRefetched] = useState(false);
  const [isPollingUnprocessedPhotos, setIsPollingUnprocessedPhotos] = useState(
    false,
  );

  const {
    data: { album: unprocessedPhotosSlideshowAlbum } = {},
    startPolling: startPollingUnprocessedPhotos,
    stopPolling: stopPollingUnprocessedPhotos,
  } = useQuery(AlbumSlideshowForUnprocessedPhotos, {
    variables: { id: album?.id, include_slideshow_photo_data: includePhotos },
    fetchPolicy: 'network-only',
    skip:
      !album?.id ||
      isMemoryBook(album) ||
      [ALBUM_SLIDESHOW_MODE.ALL_VIDEOS].includes(slideshowMode),
  });

  const { photos: pollingPhotos, unprocessed_photos: unprocessedPhotos } =
    unprocessedPhotosSlideshowAlbum?.slideshow ?? {};

  const onStopPollingUnprocessedPhotos = ({
    onPollingFinished: onPollingFinishedProp,
  }) => {
    if (!album?.id) {
      return;
    }

    if ([ALBUM_SLIDESHOW_MODE.ALL_VIDEOS].includes(slideshowMode)) {
      return;
    }
    stopPollingUnprocessedPhotos();
    setIsPollingUnprocessedPhotos(false);
    onPollingFinishedProp?.();
  };

  const onStartPollingUnprocessedPhotos = ({
    pollingIntervalMs: pollingIntervalMsProp,
  }) => {
    if (!album?.id) {
      return;
    }

    if ([ALBUM_SLIDESHOW_MODE.ALL_VIDEOS].includes(slideshowMode)) {
      return;
    }
    startPollingUnprocessedPhotos(pollingIntervalMsProp);
    setIsPollingUnprocessedPhotos(true);
  };

  useEffect(() => {
    if (pollingIntervalMs) {
      onStartPollingUnprocessedPhotos({ pollingIntervalMs });
    }
  }, [pollingIntervalMs, album, slideshowMode]);

  useEffect(() => {
    if (typeof pollingPhotos?.total !== 'undefined') {
      setTotalPhotos(pollingPhotos?.total);
    }
  }, [pollingPhotos]);

  useEffect(() => {
    if (isPollingUnprocessedPhotos) {
      stopPollingUnprocessedPhotos();
      startPollingUnprocessedPhotos(pollingIntervalMs);
    }
  }, [includePhotos]);

  // We would only like to stop including photos if the user has refetched.
  useEffect(() => {
    if (unprocessedPhotos?.total === 0 && hasRefetched) {
      // Stop fetching photo data if there are no more unprocessed
      setIncludePhotos(false);
    }
  }, [unprocessedPhotos, hasRefetched]);

  useEffect(() => {
    if (lastUploadedAt && lastUploadedAt < slideshowLastUploadedAt) {
      setHasRefetched(false);
      setIncludePhotos(true);
    }
  }, [lastUploadedAt, slideshowLastUploadedAt]);

  useEffect(() => {
    if (unprocessedPhotosSlideshowAlbum?.last_uploaded_at !== 'undefined') {
      setLastUploadedAt(unprocessedPhotosSlideshowAlbum?.last_uploaded_at);
    }
  }, [unprocessedPhotosSlideshowAlbum?.last_uploaded_at]);

  return {
    totalPhotos,
    totalUnprocessedPhotos: unprocessedPhotos?.total ?? 0,
    isPollingUnprocessedPhotos,
    onStartPollingUnprocessedPhotos,
    onStopPollingUnprocessedPhotos,
    setHasRefetched,
  };
};

export default useAlbumSlideshowForUnprocessedPhotos;
